import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingBilling() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('records');
  const [clients, setClients] = useState([]);

  // Generate form
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState({ outsourcing_client_id:'', billing_month: new Date().getMonth()+1, billing_year: new Date().getFullYear() });

  // Adjustment form
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState({ outsourcing_billing_record_id:'', adjustment_type:'', direction:'positive', amount:'', reason:'', notes:'' });

  // Extra service form
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [extraForm, setExtraForm] = useState({ outsourcing_service_id:'', amount:'', is_recurring:false, notes:'' });
  const [availableExtras, setAvailableExtras] = useState([]);

  // Comparison
  const [comparison, setComparison] = useState(null);

  useEffect(() => { if (companyId) { fetchPeriods(); loadClients(); } }, [companyId, page, clientFilter, statusFilter, monthFilter, yearFilter]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchPeriods = async () => {
    setLoading(true);
    let url = `${API}/companies/${companyId}/outsourcing/billing?page=${page}`;
    if (clientFilter) url += `&client_id=${clientFilter}`;
    if (statusFilter) url += `&billing_status=${statusFilter}`;
    if (monthFilter) url += `&month=${monthFilter}`;
    if (yearFilter) url += `&year=${yearFilter}`;
    const res = await authFetch(url);
    if (!res) return;
    const json = await res.json();
    setPeriods(json.data || []);
    setLastPage(json.last_page || 1);
    setLoading(false);
  };

  const loadClients = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients?status=active`);
    if (res) { const j = await res.json(); setClients(j.data || []); }
  };

  const openPeriod = async (p) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${p.id}`);
    if (!res) return;
    setSelected(await res.json());
    setTab('records');
    setComparison(null);
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${selected.id}`);
    if (res) setSelected(await res.json());
  };

  // ── GENERATE ──
  const generateBilling = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/generate`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(genForm) });
    if (!res) return;
    if (res.ok) { const p = await res.json(); setShowGenerate(false); setSelected(p); setTab('records'); fetchPeriods(); }
    else { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  // ── WORKFLOW ──
  const doWorkflow = async (action) => {
    let url = `${API}/companies/${companyId}/outsourcing/billing/${selected.id}/${action}`;
    let opts = { method:'POST' };
    if (action === 'reopen') {
      const reason = window.prompt('Reason for reopening:');
      if (!reason) return;
      opts.headers = {'Content-Type':'application/json'};
      opts.body = JSON.stringify({reason});
    }
    const res = await authFetch(url, opts);
    if (res && res.ok) { refreshSelected(); fetchPeriods(); }
    else if (res) { const e = await res.json(); window.alert(e.message || JSON.stringify(e)); }
  };

  // ── ADJUSTMENT ──
  const addAdjustment = async () => {
    const payload = { ...adjForm };
    if (!payload.outsourcing_billing_record_id) delete payload.outsourcing_billing_record_id;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${selected.id}/adjustments`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (res && res.ok) { setShowAdjForm(false); setAdjForm({outsourcing_billing_record_id:'',adjustment_type:'',direction:'positive',amount:'',reason:'',notes:''}); refreshSelected(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  // ── EXTRA SERVICE ──
  const loadExtras = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/services?service_type=extra&status=active`);
    if (res) { const j = await res.json(); setAvailableExtras(j.data || []); }
  };

  const addExtra = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${selected.id}/extras`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(extraForm) });
    if (res && res.ok) { setShowExtraForm(false); setExtraForm({outsourcing_service_id:'',amount:'',is_recurring:false,notes:''}); refreshSelected(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const removeExtra = async (extraId) => {
    if (!window.confirm('Remove this extra service?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${selected.id}/extras/${extraId}`, { method:'DELETE' });
    if (res && res.ok) refreshSelected();
  };

  // ── COMPARISON ──
  const loadComparison = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${selected.id}/comparison`);
    if (res) setComparison(await res.json());
  };

  // ── EXPORT ──
  const exportBilling = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/billing/${selected.id}/export`);
    if (!res) return;
    const data = await res.json();
    const csv = [Object.keys(data.rows[0]||{}).join(','), ...(data.rows||[]).map(r => Object.values(r).join(','))].join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `billing_${selected.id}.csv`;
    a.click();
  };

  if (!companyId) return <div style={st.page}><p>Please select a company first.</p></div>;

  // ── DETAIL VIEW ──
  if (selected) {
    const p = selected;
    const editable = p.billing_status === 'draft' || p.billing_status === 'reviewed';

    return (
      <div style={st.page}>
        <button onClick={() => { setSelected(null); fetchPeriods(); }} style={st.backBtn}>← Back to Billing</button>
        <div style={st.detailHeader}>
          <div>
            <h2 style={st.title}>{p.client?.name || '—'} — {p.billing_month}/{p.billing_year} <span style={{fontSize:14,color:'#999'}}>v{p.version}</span></h2>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{...st.badge, backgroundColor:billingClr(p.billing_status)}}>{p.billing_status}</span>
            {p.billing_status === 'draft' && <button onClick={() => doWorkflow('review')} style={st.btnPrimary}>Review</button>}
            {p.billing_status === 'reviewed' && <button onClick={() => doWorkflow('approve')} style={{...st.btnPrimary,background:'#27ae60'}}>Approve</button>}
            {p.billing_status === 'approved' && <button onClick={() => doWorkflow('reopen')} style={st.btnWarn}>Reopen</button>}
            <button onClick={exportBilling} style={st.btnSm}>Export CSV</button>
          </div>
        </div>

        {/* Warnings */}
        {p.warnings && p.warnings.length > 0 && (
          <div style={{background:'#fff3cd',border:'1px solid #ffc107',borderRadius:8,padding:14,marginBottom:16}}>
            <strong>⚠ Warnings:</strong>
            <ul style={{margin:'6px 0 0 16px',padding:0}}>{p.warnings.map((w,i) => <li key={i} style={{fontSize:13}}>{w}</li>)}</ul>
          </div>
        )}

        {/* Summary */}
        <div style={{...st.section,display:'flex',gap:24,flexWrap:'wrap',justifyContent:'space-around'}}>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Standard Services</div><div style={{fontSize:22,fontWeight:700}}>${Number(p.total_standard_services||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Extra Services</div><div style={{fontSize:22,fontWeight:700}}>${Number(p.total_extra_services||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Adjustments</div><div style={{fontSize:22,fontWeight:700}}>${Number(p.total_adjustments||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
          <div style={{textAlign:'center'}}><div style={{fontSize:13,color:'#888'}}>Total Billable</div><div style={{fontSize:26,fontWeight:700,color:'#27ae60'}}>${Number(p.total_billable||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div></div>
        </div>

        {/* Tabs */}
        <div style={st.tabs}>
          {['records','extras','adjustments','comparison','info'].map(t => (
            <button key={t} onClick={() => { setTab(t); if (t==='comparison') loadComparison(); }} style={tab===t ? st.tabActive : st.tab}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>
          ))}
        </div>

        {/* Records Tab */}
        {tab === 'records' && (
          <div style={st.section}>
            <h3 style={{marginTop:0}}>Employee Billing Records</h3>
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>Employee</th>
                    <th style={st.th}>Service</th>
                    <th style={st.th}>Salary</th>
                    <th style={st.th}>Rate</th>
                    <th style={st.th}>Transport</th>
                    <th style={st.th}>Insurance</th>
                    <th style={st.th}>Proration</th>
                    <th style={st.th}>Adj.</th>
                    <th style={st.th}>Total</th>
                    {p.has_warnings && <th style={st.th}>⚠</th>}
                  </tr>
                </thead>
                <tbody>
                  {(p.records || []).map((r, i) => (
                    <tr key={r.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                      <td style={st.td}>{r.employee?.name || `#${r.employee_id}`}</td>
                      <td style={st.td}>{r.service?.name || '—'}</td>
                      <td style={st.td}>${Number(r.outsourcing_salary||0).toLocaleString()}</td>
                      <td style={st.td}>${Number(r.billing_rate||0).toLocaleString()}</td>
                      <td style={st.td}>${Number(r.transportation_amount||0).toLocaleString()}</td>
                      <td style={st.td}>${Number(r.insurance_amount||0).toLocaleString()}</td>
                      <td style={st.td}>{(r.proration_factor * 100).toFixed(0)}%</td>
                      <td style={st.td}>${Number(r.adjustments_total||0).toLocaleString()}</td>
                      <td style={{...st.td,fontWeight:600}}>${Number(r.employee_billable_amount||0).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                      {p.has_warnings && <td style={st.td}>{r.has_warnings ? '⚠' : '—'}</td>}
                    </tr>
                  ))}
                  {(p.records || []).length === 0 && <tr><td colSpan={9} style={{...st.td,textAlign:'center',color:'#999'}}>No records</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Extras Tab */}
        {tab === 'extras' && (
          <div style={st.section}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <h3 style={{margin:0}}>Extra Services</h3>
              {editable && <button onClick={() => { loadExtras(); setExtraForm({outsourcing_service_id:'',amount:'',is_recurring:false,notes:''}); setShowExtraForm(true); }} style={st.btnPrimary}>+ Add Extra</button>}
            </div>
            {(p.active_extra_services || []).length === 0 && <p style={{color:'#999'}}>No extra services.</p>}
            {(p.active_extra_services || []).map(e => (
              <div key={e.id} style={st.listItem}>
                <div>
                  <strong>{e.service?.name || '—'}</strong> <span style={{fontSize:13,color:'#999'}}>({e.service?.code})</span>
                  <div style={{fontSize:13}}>${Number(e.amount).toLocaleString('en-US',{minimumFractionDigits:2})} · {e.is_recurring ? 'Recurring' : 'One-time'}</div>
                  {e.notes && <div style={{fontSize:12,color:'#999'}}>{e.notes}</div>}
                </div>
                {editable && <button onClick={() => removeExtra(e.id)} style={{...st.btnSm,color:'#e74c3c'}}>Remove</button>}
              </div>
            ))}
          </div>
        )}

        {/* Adjustments Tab */}
        {tab === 'adjustments' && (
          <div style={st.section}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <h3 style={{margin:0}}>Adjustments</h3>
              {editable && <button onClick={() => { setAdjForm({outsourcing_billing_record_id:'',adjustment_type:'',direction:'positive',amount:'',reason:'',notes:''}); setShowAdjForm(true); }} style={st.btnPrimary}>+ Add Adjustment</button>}
            </div>
            {(p.adjustments || []).length === 0 && <p style={{color:'#999'}}>No adjustments.</p>}
            {(p.adjustments || []).map(a => (
              <div key={a.id} style={st.listItem}>
                <div>
                  <strong>{a.adjustment_type}</strong> <span style={{color: a.direction==='positive' ? '#27ae60' : '#e74c3c', fontWeight:600}}>{a.direction === 'positive' ? '+' : '-'}${Number(a.amount).toLocaleString('en-US',{minimumFractionDigits:2})}</span>
                  <div style={{fontSize:13}}>{a.reason}</div>
                  {a.notes && <div style={{fontSize:12,color:'#999'}}>{a.notes}</div>}
                  <div style={{fontSize:12,color:'#999'}}>By: {a.created_by_user?.name || '—'} · {a.outsourcing_billing_record_id ? `Employee record #${a.outsourcing_billing_record_id}` : 'Client-level'}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Comparison Tab */}
        {tab === 'comparison' && (
          <div style={st.section}>
            <h3 style={{marginTop:0}}>Month-to-Month Comparison</h3>
            {!comparison ? <p>Loading...</p> : !comparison.previous ? <p style={{color:'#999'}}>No previous month data available.</p> : (
              <div style={st.tableWrap}>
                <table style={st.table}>
                  <thead>
                    <tr><th style={st.th}>Metric</th><th style={st.th}>{comparison.previous_month}</th><th style={st.th}>{comparison.current_month}</th><th style={st.th}>Change</th></tr>
                  </thead>
                  <tbody>
                    {[
                      ['Employees', comparison.previous.employees, comparison.current.employees],
                      ['Standard Services', comparison.previous.total_standard, comparison.current.total_standard],
                      ['Extra Services', comparison.previous.total_extra, comparison.current.total_extra],
                      ['Adjustments', comparison.previous.total_adjustments, comparison.current.total_adjustments],
                      ['Total Billable', comparison.previous.total_billable, comparison.current.total_billable],
                    ].map(([label, prev, curr], i) => {
                      const diff = curr - prev;
                      return (
                        <tr key={i} style={i%2===0 ? st.rowEven : st.rowOdd}>
                          <td style={{...st.td,fontWeight:600}}>{label}</td>
                          <td style={st.td}>{typeof prev === 'number' && label !== 'Employees' ? `$${Number(prev).toLocaleString('en-US',{minimumFractionDigits:2})}` : prev}</td>
                          <td style={st.td}>{typeof curr === 'number' && label !== 'Employees' ? `$${Number(curr).toLocaleString('en-US',{minimumFractionDigits:2})}` : curr}</td>
                          <td style={{...st.td, color: diff > 0 ? '#27ae60' : diff < 0 ? '#e74c3c' : '#999'}}>{diff > 0 ? '+' : ''}{typeof diff === 'number' && label !== 'Employees' ? `$${Number(diff).toLocaleString('en-US',{minimumFractionDigits:2})}` : diff}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Info Tab */}
        {tab === 'info' && (
          <div style={st.section}>
            <h3 style={{marginTop:0}}>Billing Period Info</h3>
            <div style={st.grid2}>
              <div><strong>Generated By:</strong> {p.generated_by_user?.name || '—'}</div>
              <div><strong>Reviewed By:</strong> {p.reviewed_by_user?.name || '—'} {p.reviewed_at ? `(${new Date(p.reviewed_at).toLocaleString()})` : ''}</div>
              <div><strong>Approved By:</strong> {p.approved_by_user?.name || '—'} {p.approved_at ? `(${new Date(p.approved_at).toLocaleString()})` : ''}</div>
              <div><strong>Accounting Status:</strong> {p.accounting_status}</div>
              <div><strong>Handoff Status:</strong> {p.handoff_status || '—'}</div>
              <div><strong>Version:</strong> {p.version}</div>
              {p.reopen_reason && <div style={{gridColumn:'1/3'}}><strong>Reopen Reason:</strong> {p.reopen_reason}</div>}
              {p.previous_approved_total && <div><strong>Previous Approved Total:</strong> ${Number(p.previous_approved_total).toLocaleString('en-US',{minimumFractionDigits:2})}</div>}
            </div>
          </div>
        )}

        {/* Modals */}
        {showAdjForm && (
          <div style={st.overlay} onClick={() => setShowAdjForm(false)}>
            <div style={st.modal} onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}><h3 style={{margin:0}}>Add Adjustment</h3><button onClick={() => setShowAdjForm(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button></div>
              <label style={st.label}>Employee Record (optional — leave blank for client-level)</label>
              <select value={adjForm.outsourcing_billing_record_id} onChange={e => setAdjForm({...adjForm,outsourcing_billing_record_id:e.target.value})} style={st.input}>
                <option value="">Client-level</option>
                {(p.records || []).map(r => <option key={r.id} value={r.id}>{r.employee?.name || `#${r.employee_id}`}</option>)}
              </select>
              <label style={st.label}>Type *</label>
              <input value={adjForm.adjustment_type} onChange={e => setAdjForm({...adjForm,adjustment_type:e.target.value})} style={st.input} placeholder="e.g. Bonus, Penalty, Correction" />
              <label style={st.label}>Direction *</label>
              <select value={adjForm.direction} onChange={e => setAdjForm({...adjForm,direction:e.target.value})} style={st.input}>
                <option value="positive">Positive (+)</option>
                <option value="negative">Negative (−)</option>
              </select>
              <label style={st.label}>Amount *</label>
              <input type="number" step="0.01" value={adjForm.amount} onChange={e => setAdjForm({...adjForm,amount:e.target.value})} style={st.input} />
              <label style={st.label}>Reason *</label>
              <input value={adjForm.reason} onChange={e => setAdjForm({...adjForm,reason:e.target.value})} style={st.input} />
              <label style={st.label}>Notes</label>
              <textarea value={adjForm.notes} onChange={e => setAdjForm({...adjForm,notes:e.target.value})} style={{...st.input,minHeight:50}} />
              <button onClick={addAdjustment} style={st.btnPrimary}>Add Adjustment</button>
            </div>
          </div>
        )}

        {showExtraForm && (
          <div style={st.overlay} onClick={() => setShowExtraForm(false)}>
            <div style={st.modal} onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}><h3 style={{margin:0}}>Add Extra Service</h3><button onClick={() => setShowExtraForm(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button></div>
              <label style={st.label}>Service *</label>
              <select value={extraForm.outsourcing_service_id} onChange={e => setExtraForm({...extraForm,outsourcing_service_id:e.target.value})} style={st.input}>
                <option value="">Select...</option>
                {availableExtras.map(sv => <option key={sv.id} value={sv.id}>{sv.name} ({sv.code})</option>)}
              </select>
              <label style={st.label}>Amount *</label>
              <input type="number" step="0.01" value={extraForm.amount} onChange={e => setExtraForm({...extraForm,amount:e.target.value})} style={st.input} />
              <label style={{...st.label,display:'flex',alignItems:'center',gap:8}}>
                <input type="checkbox" checked={extraForm.is_recurring} onChange={e => setExtraForm({...extraForm,is_recurring:e.target.checked})} /> Recurring (carry forward to next month)
              </label>
              <label style={st.label}>Notes</label>
              <textarea value={extraForm.notes} onChange={e => setExtraForm({...extraForm,notes:e.target.value})} style={{...st.input,minHeight:50}} />
              <button onClick={addExtra} style={st.btnPrimary}>Add</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div style={st.page}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:16}}>
        <h2 style={st.title}>Monthly Billing</h2>
        <button onClick={() => { setGenForm({outsourcing_client_id:'',billing_month:new Date().getMonth()+1,billing_year:new Date().getFullYear()}); setShowGenerate(true); }} style={st.btnPrimary}>+ Generate Billing</button>
      </div>

      <div style={st.filterRow}>
        <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setPage(1); }} style={{...st.input,width:180}}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{...st.input,width:140}}>
          <option value="">All Statuses</option>
          {['draft','reviewed','approved','invoiced','paid'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={monthFilter} onChange={e => { setMonthFilter(e.target.value); setPage(1); }} style={{...st.input,width:120}}>
          <option value="">All Months</option>
          {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{new Date(2000,i).toLocaleString('default',{month:'short'})}</option>)}
        </select>
        <select value={yearFilter} onChange={e => { setYearFilter(e.target.value); setPage(1); }} style={{...st.input,width:100}}>
          <option value="">Year</option>
          {[2024,2025,2026,2027,2028].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {initialLoading ? <p>Loading...</p> : periods.length === 0 ? <p style={{color:'#999'}}>No billing periods found.</p> : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Client</th>
                <th style={st.th}>Month</th>
                <th style={st.th}>Version</th>
                <th style={st.th}>Status</th>
                <th style={st.th}>Total</th>
                <th style={st.th}>Accounting</th>
                <th style={st.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, i) => (
                <tr key={p.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                  <td style={st.td}>{p.client?.name || '—'}</td>
                  <td style={st.td}>{p.billing_month}/{p.billing_year}</td>
                  <td style={st.td}>v{p.version}</td>
                  <td style={st.td}><span style={{...st.badge,backgroundColor:billingClr(p.billing_status)}}>{p.billing_status}</span></td>
                  <td style={st.td}>${Number(p.total_billable||0).toLocaleString('en-US',{minimumFractionDigits:2})}</td>
                  <td style={st.td}><span style={{...st.badge,backgroundColor:acctClr(p.accounting_status)}}>{p.accounting_status}</span></td>
                  <td style={st.td}><button onClick={() => openPeriod(p)} style={st.btnSm}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div style={st.pagination}>
          <button disabled={page<=1} onClick={() => setPage(page-1)} style={st.pageBtn}>Prev</button>
          <span>Page {page} of {lastPage}</span>
          <button disabled={page>=lastPage} onClick={() => setPage(page+1)} style={st.pageBtn}>Next</button>
        </div>
      )}

      {showGenerate && (
        <div style={st.overlay} onClick={() => setShowGenerate(false)}>
          <div style={st.modal} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}><h3 style={{margin:0}}>Generate Billing</h3><button onClick={() => setShowGenerate(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button></div>
            <label style={st.label}>Client *</label>
            <select value={genForm.outsourcing_client_id} onChange={e => setGenForm({...genForm,outsourcing_client_id:e.target.value})} style={st.input}>
              <option value="">Select Client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
            </select>
            <label style={st.label}>Month *</label>
            <select value={genForm.billing_month} onChange={e => setGenForm({...genForm,billing_month:+e.target.value})} style={st.input}>
              {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{new Date(2000,i).toLocaleString('default',{month:'long'})}</option>)}
            </select>
            <label style={st.label}>Year *</label>
            <select value={genForm.billing_year} onChange={e => setGenForm({...genForm,billing_year:+e.target.value})} style={st.input}>
              {[2024,2025,2026,2027,2028].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={generateBilling} style={{...st.btnPrimary,marginTop:8}}>Generate</button>
          </div>
        </div>
      )}
    </div>
  );
}

function billingClr(s) {
  const m = { draft:'#95a5a6', reviewed:'#3498db', approved:'#27ae60', invoiced:'#8e44ad', paid:'#2ecc71' };
  return m[s] || '#95a5a6';
}
function acctClr(s) {
  const m = { not_sent:'#95a5a6', sent:'#3498db', invoice_created:'#2980b9', invoiced:'#8e44ad', paid:'#27ae60' };
  return m[s] || '#95a5a6';
}

const st = {
  page: { padding:24, maxWidth:1200, margin:'0 auto' },
  title: { fontSize:24, fontWeight:700, margin:0 },
  filterRow: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  input: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14, width:'100%', boxSizing:'border-box', marginBottom:8 },
  label: { display:'block', fontSize:13, fontWeight:600, marginBottom:4, marginTop:8 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  btnPrimary: { padding:'8px 16px', background:'#3498db', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:600 },
  btnWarn: { padding:'8px 16px', background:'#f39c12', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14 },
  btnSm: { padding:'4px 10px', background:'#ecf0f1', border:'none', borderRadius:4, cursor:'pointer', fontSize:13 },
  backBtn: { background:'none', border:'none', color:'#3498db', cursor:'pointer', fontSize:14, fontWeight:600, marginBottom:12, padding:0 },
  detailHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:16 },
  tabs: { display:'flex', gap:0, borderBottom:'2px solid #ecf0f1', marginBottom:16 },
  tab: { padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#999', borderBottom:'2px solid transparent' },
  tabActive: { padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#3498db', fontWeight:600, borderBottom:'2px solid #3498db' },
  grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' },
  listItem: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f0f0f0', flexWrap:'wrap', gap:8 },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'#fff', borderRadius:12, padding:24, width:'90%', maxWidth:500, maxHeight:'85vh', overflowY:'auto' },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:16, marginTop:16 },
  pageBtn: { padding:'6px 14px', border:'1px solid #ccc', borderRadius:6, background:'#fff', cursor:'pointer' },
};
